import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';

// Import orphan prevention utility
import { preventOrphans } from '../utils/textUtils';

const ArtworkPage = ({ assets, baseUrl }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State for image loading (tiny -> full)
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState('');

  // State for selected image (0 = main, 1+ = extra images)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Check if user came from carousel
  const searchParams = new URLSearchParams(location.search);
  const fromCarousel = searchParams.get('from') === 'carousel';

  // Scroll to top when component mounts or slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
    // Reset to main image when artwork changes
    setSelectedImageIndex(0);
  }, [slug]);

  // Filter assets to only include artwork with "art" prefix
  const artworkAssets = assets.filter(asset => {
    const artworkId = asset['artwork-id'] || asset.filename.split('_')[0];
    return artworkId.startsWith('art');
  });

  // Find current artwork by slug or fallback to filename-based matching
  const currentIndex = artworkAssets.findIndex(asset => {
    if (asset.slug) {
      // Normalize both slugs: convert underscores to hyphens and lowercase
      const normalizedAssetSlug = asset.slug.replace(/_/g, '-').toLowerCase();
      const normalizedUrlSlug = slug.toLowerCase();
      return normalizedAssetSlug === normalizedUrlSlug;
    } else {
      // Fallback: create slug from filename for existing data
      const filename = asset.filename || '';
      const generatedSlug = filename
        .replace(/\.(jpg|jpeg|png|gif|webp)$/i, '') // Remove extension
        .replace(/^[^_]+_/, '') // Remove art001_ prefix
        .replace(/_/g, '-') // Replace underscores with hyphens
        .toLowerCase();
      return generatedSlug === slug;
    }
  });
  const currentArtwork = artworkAssets[currentIndex];

  // Generate list of all images (main + extra images)
  const allImages = React.useMemo(() => {
    if (!currentArtwork) return [];

    const extraImagesCount = parseInt(currentArtwork['extra-images'] || '0');
    const images = [];

    // Main image (index 0)
    images.push({
      full: currentArtwork.filename,
      thumbnail: currentArtwork.thumbnail,
      index: 0
    });

    // Extra images (index 1+)
    if (extraImagesCount > 0) {
      const ext = currentArtwork.filename.substring(currentArtwork.filename.lastIndexOf('.'));
      const nameWithoutExt = currentArtwork.filename.substring(0, currentArtwork.filename.lastIndexOf('.'));

      for (let i = 1; i <= extraImagesCount; i++) {
        const paddedNumber = i.toString().padStart(2, '0');
        images.push({
          full: `${nameWithoutExt}_${paddedNumber}${ext}`,
          thumbnail: `${nameWithoutExt}_${paddedNumber}_th${ext}`,
          index: i
        });
      }
    }

    return images;
  }, [currentArtwork]);

  // Helper function to generate tiny image URL
  const getTinyImageUrl = useCallback((filename) => {
    const ext = filename.substring(filename.lastIndexOf('.'));
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    return `${baseUrl}${nameWithoutExt}_tiny${ext}`;
  }, [baseUrl]);

  // Image loading effect: tiny -> full
  useEffect(() => {
    if (!currentArtwork || allImages.length === 0) return;

    const selectedImage = allImages[selectedImageIndex];
    if (!selectedImage) return;

    const fullImageUrl = `${baseUrl}${selectedImage.full}`;
    const tinyImageUrl = getTinyImageUrl(selectedImage.full);

    // Reset loading state
    setImageLoaded(false);
    setCurrentImageUrl(tinyImageUrl);

    // Preload the full image
    const img = new Image();
    img.src = fullImageUrl;
    img.onload = () => {
      setCurrentImageUrl(fullImageUrl);
      setImageLoaded(true);
    };
  }, [currentArtwork, baseUrl, getTinyImageUrl, allImages, selectedImageIndex]);

  // Set SEO title when artwork loads
  useEffect(() => {
    if (currentArtwork) {
      const seoTitle = `${currentArtwork.title}${currentArtwork.year ? ` - Original ${currentArtwork.year}` : ' - Original'} ${currentArtwork.type || 'Artwork'} by DYAR (Khoa Tran)`;
      document.title = seoTitle;
    }

    // Reset to default title on unmount
    return () => {
      document.title = 'DYAR Arts';
    };
  }, [currentArtwork]);

  // Set Open Graph, Twitter Card, Meta Description, and Canonical URL meta tags
  useEffect(() => {
    if (!currentArtwork) return;

    // Generate OG image URL based on og-image field
    // If og-image is "TRUE": use og_[artwork-id]_[slug].jpg
    // If og-image is "FALSE" or empty: use default og_dyar_arts.jpg
    let ogImageUrl;
    const hasCustomOgImage = currentArtwork['og-image'] === 'TRUE';

    if (hasCustomOgImage) {
      // Construct artwork-specific OG image: og_art016_happily_corrupted.jpg
      const artworkId = currentArtwork['artwork-id'] || '';
      const slug = currentArtwork.slug || '';
      const ext = currentArtwork.filename ? currentArtwork.filename.substring(currentArtwork.filename.lastIndexOf('.')) : '.jpg';
      ogImageUrl = `${baseUrl}/art/og_${artworkId}_${slug}${ext}`;
    } else {
      // Use default OG image
      ogImageUrl = `${baseUrl}/art/og_dyar_arts.jpg`;
    }

    // Build SEO-optimized title and description
    const ogTitle = `${currentArtwork.title} - Original ${currentArtwork.year || ''} ${currentArtwork.type || 'Artwork'} by DYAR (Khoa Tran)`.trim();
    const ogDescription = currentArtwork.description || currentArtwork['alt-text'] || `Original ${currentArtwork.type || 'artwork'} by DYAR (Khoa Tran)`;

    // Get current page URL
    const currentUrl = window.location.href;
    const canonicalUrl = window.location.origin + window.location.pathname;

    // Helper function to set or create meta tag
    const setMetaTag = (attribute, attributeValue, content) => {
      let tag = document.querySelector(`meta[${attribute}="${attributeValue}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, attributeValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // Meta description (for search engines)
    setMetaTag('name', 'description', ogDescription);

    // Open Graph tags (for social media)
    setMetaTag('property', 'og:title', ogTitle);
    setMetaTag('property', 'og:description', ogDescription);
    setMetaTag('property', 'og:image', ogImageUrl);
    setMetaTag('property', 'og:url', currentUrl);
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:site_name', 'DYAR Arts');

    // Twitter Card tags
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', ogTitle);
    setMetaTag('name', 'twitter:description', ogDescription);
    setMetaTag('name', 'twitter:image', ogImageUrl);

    // Canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = canonicalUrl;

    // Cleanup function to remove tags on unmount
    return () => {
      // Remove meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.remove();

      // Remove Open Graph tags
      ['og:title', 'og:description', 'og:image', 'og:url', 'og:type', 'og:site_name'].forEach(prop => {
        const tag = document.querySelector(`meta[property="${prop}"]`);
        if (tag) tag.remove();
      });

      // Remove Twitter Card tags
      ['twitter:card', 'twitter:title', 'twitter:description', 'twitter:image'].forEach(name => {
        const tag = document.querySelector(`meta[name="${name}"]`);
        if (tag) tag.remove();
      });

      // Remove canonical link
      if (linkCanonical && linkCanonical.parentNode) {
        linkCanonical.remove();
      }
    };
  }, [currentArtwork, baseUrl]);

  // Preload next and previous images for smooth transitions (with looping)
  useEffect(() => {
    if (!currentArtwork) return;

    const imagesToPreload = [];

    // Add previous image (loop to last if at first)
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : artworkAssets.length - 1;
    const prevArtwork = artworkAssets[prevIndex];
    if (prevArtwork?.filename) {
      imagesToPreload.push(`${baseUrl}${prevArtwork.filename}`);
    }

    // Add next image (loop to first if at last)
    const nextIndex = currentIndex < artworkAssets.length - 1 ? currentIndex + 1 : 0;
    const nextArtwork = artworkAssets[nextIndex];
    if (nextArtwork?.filename) {
      imagesToPreload.push(`${baseUrl}${nextArtwork.filename}`);
    }

    // Preload images
    imagesToPreload.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [currentIndex, currentArtwork, artworkAssets, baseUrl]);

  // Determine if we're at the first or last artwork
  const isFirstArtwork = currentIndex === 0;
  const isLastArtwork = currentIndex === artworkAssets.length - 1;

  // Handle back to gallery navigation
  const handleBackToGallery = (e) => {
    e.preventDefault();
    navigate('/');
  };

  // Handle case where artwork is not found
  if (!currentArtwork) {
    return (
      <div className="artwork-page-container">
        <div className="artwork-not-found">
          <h1>Artwork Not Found</h1>
          <p>Sorry, we couldn't find the artwork you're looking for.</p>
        </div>
      </div>
    );
  }

  // Get previous and next artworks (with looping)
  const previousArtwork = currentIndex > 0 ? artworkAssets[currentIndex - 1] : artworkAssets[artworkAssets.length - 1];
  const nextArtwork = currentIndex < artworkAssets.length - 1 ? artworkAssets[currentIndex + 1] : artworkAssets[0];

  // Helper function to generate slug from asset
  const getAssetSlug = (asset) => {
    let slug;
    if (asset.slug) {
      // Normalize slug: convert underscores to hyphens and lowercase
      slug = asset.slug.replace(/_/g, '-').toLowerCase();
    } else {
      // Fallback: generate slug from filename
      const filename = asset.filename || '';
      slug = filename
        .replace(/\.(jpg|jpeg|png|gif|webp)$/i, '') // Remove extension
        .replace(/^[^_]+_/, '') // Remove art001_ prefix
        .replace(/_/g, '-') // Replace underscores with hyphens
        .toLowerCase();
    }
    // Preserve the ?from=carousel parameter if present
    return fromCarousel ? `${slug}?from=carousel` : slug;
  };

  // Build image URL - use selected image instead of main image
  const selectedImage = allImages[selectedImageIndex] || allImages[0] || { full: currentArtwork.filename };
  const imageUrl = `${baseUrl}${selectedImage.full}`;
  const isHorizontal = currentArtwork.orientation === 'horizontal';

  // Check if artwork is featured (appears in hero carousel)
  const isFeatured = currentArtwork['hero-assets'] && currentArtwork['hero-assets'] !== 'none';

  // Build structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "VisualArtwork",
    "name": currentArtwork.title,
    "image": imageUrl,
    "description": currentArtwork.description || currentArtwork['alt-text'] || currentArtwork.title,
    "artform": currentArtwork.type || "Artwork",
    "artMedium": currentArtwork.medium || currentArtwork.type || "Mixed Media",
    "creator": {
      "@type": "Person",
      "name": "DYAR (Khoa Tran)",
      "alternateName": "Khoa Tran"
    },
    "copyrightHolder": {
      "@type": "Person",
      "name": "DYAR (Khoa Tran)"
    },
    "copyrightYear": currentArtwork.year || new Date().getFullYear().toString(),
    "url": window.location.href
  };

  // Add optional fields if they exist
  if (currentArtwork.year) {
    structuredData.dateCreated = currentArtwork.year;
  }
  if (currentArtwork.dimensions) {
    structuredData.size = currentArtwork.dimensions;
  }
  if (currentArtwork.surface) {
    structuredData.artworkSurface = currentArtwork.surface;
  }
  if (isFeatured) {
    structuredData.keywords = "featured artwork, DYAR art, original painting";
  }

  return (
    <div className="artwork-page-container">
      {/* Schema.org Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <div className="artwork-page-content">
        {/* Art Header - Title and Description in 2-row grid */}
        <div className="art-header">
          <div className="art-header-top">
            {/* Artwork Title */}
            <h1>{preventOrphans(currentArtwork.title)}</h1>
          </div>

          <div className="art-header-bottom">
            {/* Artwork Description */}
            {currentArtwork.description && (
              <p>{preventOrphans(currentArtwork.description)}</p>
            )}
          </div>
        </div>

        {/* Art Wrapper - Asset with Prev/Next buttons */}
        <div className="art-wrapper">
            {/* Previous Button - Desktop (positioned on side) */}
            <Link
              to={`/art/${getAssetSlug(previousArtwork)}`}
              className="nav-btn-desktop prev-btn-desktop"
            >
              {previousArtwork.thumbnail ? (
                <img
                  src={`${baseUrl}${previousArtwork.thumbnail}`}
                  alt={`Previous: ${previousArtwork['alt-text'] || previousArtwork.title || 'artwork'}`}
                  className="nav-thumbnail-desktop"
                />
              ) : (
                <div className="nav-thumbnail-placeholder-desktop">
                  {/* Placeholder for thumbnail */}
                </div>
              )}
              <span className="nav-arrow-desktop">←</span>
            </Link>

            {/* Next Button - Desktop (positioned on side) */}
            <Link
              to={`/art/${getAssetSlug(nextArtwork)}`}
              className="nav-btn-desktop next-btn-desktop"
            >
              {nextArtwork.thumbnail ? (
                <img
                  src={`${baseUrl}${nextArtwork.thumbnail}`}
                  alt={`Next: ${nextArtwork['alt-text'] || nextArtwork.title || 'artwork'}`}
                  className="nav-thumbnail-desktop"
                />
              ) : (
                <div className="nav-thumbnail-placeholder-desktop"></div>
              )}
              <span className="nav-arrow-desktop">→</span>
            </Link>

            {/* Mobile navigation buttons (above image) */}
            <div className="nav-buttons-mobile">
              <Link
                to={`/art/${getAssetSlug(previousArtwork)}`}
                className="nav-btn-responsive prev-btn-responsive"
              >
                {previousArtwork.thumbnail ? (
                  <img
                    src={`${baseUrl}${previousArtwork.thumbnail}`}
                    alt={`Previous: ${previousArtwork['alt-text'] || previousArtwork.title || 'artwork'}`}
                    className="nav-thumbnail-responsive"
                  />
                ) : (
                  <div className="nav-thumbnail-placeholder-responsive"></div>
                )}
                <span className="nav-arrow-responsive">←</span>
              </Link>

              <Link
                to={`/art/${getAssetSlug(nextArtwork)}`}
                className="nav-btn-responsive next-btn-responsive"
              >
                {nextArtwork.thumbnail ? (
                  <img
                    src={`${baseUrl}${nextArtwork.thumbnail}`}
                    alt={`Next: ${nextArtwork['alt-text'] || nextArtwork.title || 'artwork'}`}
                    className="nav-thumbnail-responsive"
                  />
                ) : (
                  <div className="nav-thumbnail-placeholder-responsive"></div>
                )}
                <span className="nav-arrow-responsive">→</span>
              </Link>
            </div>

            <div
              className={`art-asset ${isHorizontal ? 'horizontal' : 'vertical'} ${!imageLoaded ? 'loading-tiny' : ''}`}
              style={{ backgroundImage: `url(${currentImageUrl})` }}
            >
              {/* Structural img provides sizing, background-image provides visual display at 110% */}
              <img
                src={imageUrl}
                alt={currentArtwork['alt-text'] || currentArtwork.title || 'Artwork'}
                className="structural-image"
              />
            </div>
        </div>

        {/* Extra Images Gallery - Only show if there are extra images */}
        {allImages.length > 1 && (
          <div className="extra-images-gallery">
            {allImages.map((image, index) => (
              <div
                key={index}
                className={`gallery-thumbnail ${selectedImageIndex === index ? 'selected' : ''}`}
                onClick={() => setSelectedImageIndex(index)}
              >
                <img
                  src={`${baseUrl}${image.thumbnail}`}
                  alt={`${currentArtwork.title} - View ${index + 1}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* Social Media Section */}
        {(currentArtwork.YouTube || currentArtwork.Instagram || currentArtwork.TikTok) && (
          <div className="social-section">
            <div className="social-text">See the process</div>
            <div className="social-buttons">
              {currentArtwork.YouTube && (
                <a
                  href={currentArtwork.YouTube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label="View on YouTube"
                >
                  <img
                    src={`${baseUrl}/images/social_youtube_icon.png`}
                    alt="YouTube icon"
                    className="social-icon"
                  />
                <div class="social-link-text">
                  <span>YouTube</span>
                  @dyararts
                </div>
                </a>
              )}
              {currentArtwork.Instagram && (
                <a
                  href={currentArtwork.Instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link instagram-link"
                  aria-label="View on Instagram"
                >
                  <img
                    src={`${baseUrl}/images/social_instagram_icon.png`}
                    alt="Instagram icon"
                    className="social-icon"
                  />
                <div className="social-link-text">
                  <span>Instagram</span>
                  @dyararts
                </div>
                </a>
              )}
              {currentArtwork.TikTok && (
                <a
                  href={currentArtwork.TikTok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link tiktok-link"
                  aria-label="View on TikTok"
                >
                  <img
                    src={`${baseUrl}/images/social_tiktok_icon.png`}
                    alt="TikTok icon"
                    className="social-icon"
                  />
                <div class="social-link-text">
                  <span>TikTok</span>
                  @dyararts
                </div>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Art Details - Metadata at bottom */}
        <div className="art-details">
          <div className="artwork-metadata">
          {(currentArtwork.month || currentArtwork.day || currentArtwork.year) && (
              <div className="artwork-meta-item">
                <span className="meta-label">Date:</span>
                <span className="meta-value">
                  {currentArtwork.month && `${currentArtwork.month} `}
                  {currentArtwork.day && `${currentArtwork.day}, `}
                  {currentArtwork.year}
                </span>
              </div>
            )}
            {currentArtwork.status && (
              <div className="artwork-meta-item">
                <span className="meta-label">Status:</span>
                <span className="meta-value">{currentArtwork.status}</span>
              </div>
            )}
            {currentArtwork.type && (
              <div className="artwork-meta-item">
                <span className="meta-label">Type:</span>
                <span className="meta-value">{currentArtwork.type}</span>
              </div>
            )}
            {currentArtwork.medium && (
              <div className="artwork-meta-item">
                <span className="meta-label">Medium:</span>
                <span className="meta-value">{currentArtwork.medium}</span>
              </div>
            )}
            {currentArtwork.dimensions && (
              <div className="artwork-meta-item">
                <span className="meta-label">Dimensions:</span>
                <span className="meta-value">{currentArtwork.dimensions} inches</span>
              </div>
            )}
            {currentArtwork.surface && (
              <div className="artwork-meta-item">
                <span className="meta-label">Surface:</span>
                <span className="meta-value">{currentArtwork.surface}</span>
              </div>
            )}
          </div>
        </div>
        <p className="artwork-copyright">© DYAR Arts LLC | Artwork by Khoa Tran{currentArtwork.year && `, ${currentArtwork.year}`}</p>
      </div>
    </div>
  );
};

export default ArtworkPage;

